import { useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { IconButton } from "@/components/common/IconButton";
import { InputField } from "@/components/common/InputField";
import type { RoadmapAssignee, RoadmapTask, RoadmapTaskInput } from "@/lib/api";
import { INPUT_STYLE, focusRingOff, focusRingOn } from "@/lib/form-styles";
import { formatFullName } from "@/lib/format-name";
import { dateOfIndex, taskInput, type DayRange } from "@/lib/roadmap";

const DESCRIPTION_ROWS = 8;

interface RoadmapTaskPanelProps {
	task: RoadmapTask;
	assignees: RoadmapAssignee[];
	/** ガントチャートの表示期間。日付はこの中に収め、バーが画面外に出ないようにする。 */
	range: DayRange;
	onSave: (input: RoadmapTaskInput) => void;
	onDelete: () => Promise<void>;
	onClose: () => void;
}

/**
 * 選択中のタスクの詳細 (名前・完了・説明・担当者・期間) を編集する右ペイン。項目ごとに確定した時点で保存する。
 * タスクを切り替えたら入力を捨てるよう、呼び出し側で key に task.id を渡す。
 */
export function RoadmapTaskPanel({
	task,
	assignees,
	range,
	onSave,
	onDelete,
	onClose,
}: RoadmapTaskPanelProps) {
	const [name, setName] = useState(task.name);
	const [description, setDescription] = useState(task.description);
	const [confirmingDelete, setConfirmingDelete] = useState(false);

	const save = (patch: Partial<RoadmapTaskInput>) => {
		onSave({ ...taskInput(task), ...patch });
	};

	const commitName = () => {
		const trimmed = name.trim();
		if (trimmed === "") setName(task.name);
		else if (trimmed !== task.name) save({ name: trimmed });
	};

	const changeDate = (field: "startDate" | "endDate", value: string) => {
		if (value === "") return;
		const next = { startDate: task.startDate, endDate: task.endDate, [field]: value };
		// 逆転させる変更は、もう一方の端を同じ日に寄せて期間を保つ。
		if (next.startDate > next.endDate) {
			if (field === "startDate") next.endDate = value;
			else next.startDate = value;
		}
		save(next);
	};

	return (
		<aside
			data-testid="roadmap-task-panel"
			style={{
				flex: "none",
				width: 320,
				display: "flex",
				flexDirection: "column",
				gap: 16,
				padding: 16,
				overflowY: "auto",
				borderLeft: "var(--pane-header-border)",
				background: "var(--surface-1)",
			}}
		>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<h3
					style={{ margin: 0, fontSize: "var(--text-ui)", fontWeight: 600, color: "var(--fg-1)" }}
				>
					タスクの詳細
				</h3>
				<IconButton icon="x" onClick={onClose} data-testid="roadmap-task-panel-close" />
			</div>

			<InputField label="名前">
				<input
					data-testid="roadmap-task-name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.nativeEvent.isComposing) e.currentTarget.blur();
					}}
					onFocus={(e) => focusRingOn(e.currentTarget)}
					onBlur={(e) => {
						focusRingOff(e.currentTarget);
						commitName();
					}}
					style={INPUT_STYLE}
				/>
			</InputField>

			<label
				style={{
					display: "flex",
					alignItems: "center",
					gap: 6,
					fontSize: "var(--text-ui)",
					color: "var(--fg-1)",
					cursor: "pointer",
					userSelect: "none",
				}}
			>
				<input
					type="checkbox"
					data-testid="roadmap-task-done"
					checked={task.done}
					onChange={(e) => save({ done: e.target.checked })}
					style={{
						width: 14,
						height: 14,
						margin: 0,
						accentColor: "var(--accent-solid)",
						cursor: "pointer",
					}}
				/>
				完了
			</label>

			<InputField label="担当者">
				<select
					className="ds-select"
					data-testid="roadmap-task-assignee"
					value={task.assigneeUserId ?? ""}
					onChange={(e) => save({ assigneeUserId: e.target.value === "" ? null : e.target.value })}
					style={{ height: 36 }}
				>
					<option value="">未割り当て</option>
					{assignees
						.filter((a) => a.active || a.id === task.assigneeUserId)
						.map((a) => (
							<option key={a.id} value={a.id} disabled={!a.active}>
								{formatFullName(a)}
								{!a.active && " (無効)"}
							</option>
						))}
				</select>
			</InputField>

			<div style={{ display: "flex", gap: 8 }}>
				<DateField
					label="開始日"
					testId="roadmap-task-start"
					value={task.startDate}
					range={range}
					onCommit={(value) => changeDate("startDate", value)}
				/>
				<DateField
					label="終了日"
					testId="roadmap-task-end"
					value={task.endDate}
					range={range}
					onCommit={(value) => changeDate("endDate", value)}
				/>
			</div>

			<InputField label="説明">
				<textarea
					data-testid="roadmap-task-description"
					value={description}
					rows={DESCRIPTION_ROWS}
					onChange={(e) => setDescription(e.target.value)}
					onFocus={(e) => focusRingOn(e.currentTarget)}
					onBlur={(e) => {
						focusRingOff(e.currentTarget);
						if (description !== task.description) save({ description });
					}}
					style={{
						...INPUT_STYLE,
						height: "auto",
						padding: "8px 10px",
						resize: "vertical",
						lineHeight: "var(--leading-body)",
					}}
				/>
			</InputField>

			<div style={{ marginTop: "auto" }}>
				<ConfirmButton
					label="タスクを削除"
					icon="trash"
					variant="destructive"
					size="sm"
					loading={false}
					onClick={() => setConfirmingDelete(true)}
					data-testid="roadmap-task-delete"
				/>
			</div>
			<ConfirmDialog
				open={confirmingDelete}
				title="タスクを削除"
				message={`「${task.name}」を削除します。元に戻せません。`}
				confirmLabel="削除"
				onConfirm={onDelete}
				onCancel={() => setConfirmingDelete(false)}
			/>
		</aside>
	);
}

/**
 * 日付の入力。年を打っている途中 (0002 年など) で保存しないよう、確定 (blur・Enter) したときだけ渡す。
 * 入力中以外は value を出すので、バーのドラッグで変わった日付もそのまま映る。
 */
function DateField({
	label,
	testId,
	value,
	range,
	onCommit,
}: {
	label: string;
	testId: string;
	value: string;
	range: DayRange;
	onCommit: (value: string) => void;
}) {
	const [editing, setEditing] = useState<string | null>(null);
	const min = dateOfIndex(range.start);
	const max = dateOfIndex(range.end);

	const commit = () => {
		if (editing !== null && editing !== "" && editing !== value) {
			onCommit(editing < min ? min : editing > max ? max : editing);
		}
		setEditing(null);
	};

	return (
		<InputField label={label}>
			<input
				type="date"
				data-testid={testId}
				value={editing ?? value}
				min={min}
				max={max}
				onFocus={(e) => {
					focusRingOn(e.currentTarget);
					setEditing(value);
				}}
				onChange={(e) => setEditing(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") e.currentTarget.blur();
				}}
				onBlur={(e) => {
					focusRingOff(e.currentTarget);
					commit();
				}}
				style={INPUT_STYLE}
			/>
		</InputField>
	);
}
